import React, { useState, useContext, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import "../pages/Collection.css";
import { PostContext } from "../context/PostContext";
import { LanguageContext } from "../LanguageContext";
import SEO from "../components/SEO";

const Collection = () => {
  const { language } = useContext(LanguageContext);
  const { pos } = useContext(PostContext);
  const [sortOrder, setSortOrder] = useState("latest");
  const [theme, setTheme] = useState("light");

  const handleClick = () => window.scrollTo(0, 0);

  // Apply saved theme or default
  useEffect(() => {
    const stored = localStorage.getItem("theme");
    if (stored) setTheme(stored);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  // Optimize post sorting
  const sortedPosts = useMemo(() => {
    return [...pos].sort((a, b) =>
      sortOrder === "latest" ? b.id - a.id : a.id - b.id
    );
  }, [pos, sortOrder]);

  // SEO
  const metaTitle = "500+ Snapseed QR Codes Collection - Free Editing Presets & Filters";
  const metaDescription = "Explore a collection of 100+ Snapseed QR codes for free. Get stunning DSLR photo editing presets, filters, and effects for your photos.";
  const metaUrl = "https://www.snapseedqrcodes.download/collection";
  const metaImage = "https://www.snapseedqrcodes.download/snapseedqrcodes_logo.webp";

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "headline": metaTitle,
    "description": metaDescription,
    "url": metaUrl,
    "image": metaImage,
    "publisher": {
      "@type": "Organization",
      "name": "Snapseed QR Codes",
      "logo": {
        "@type": "ImageObject",
        "url": metaImage
      }
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": sortedPosts.map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://www.snapseedqrcodes.download/collection/${post.title?.en.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}/${post.id}`,
        "name": post.title?.en || "Snapseed QR Preset"
      }))
    }
  };

  return (
    <div className="blog-page">
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={metaUrl}
        image={metaImage}
        schemaMarkup={schemaMarkup}
      />

      {/* 🌙 Theme toggle */}
      <button className="theme-toggle-btn" onClick={toggleTheme}>
        {theme === "light" ? "🌙 Dark Mode" : "☀️ Light Mode"}
      </button>

      <h1 className="headingblog">
        Unlock 500+ Snapseed QR Codes to Enhance Your Photos – Discover the Best Presets!
      </h1>

      <div className="sort-dropdown">
        <label htmlFor="sortOrder">Sort By: </label>
        <select
          id="sortOrder"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="latest">Latest First</option>
          <option value="oldest">Oldest First</option>
        </select>
      </div>

      <div className="blog-container">
        {sortedPosts.map((post) => {
          const postTitle = post.title?.[language] || post.title?.en || "Untitled";
          const postExcerpt = post.excerpt?.[language] || post.excerpt?.en || "No description available";

          const postSlug = post.title?.en
            ?.toLowerCase()
            ?.trim()
            ?.replace(/\s+/g, "-")
            ?.replace(/[^a-z0-9-]/g, "");

          return (
            <Link
              to={`/collection/${postSlug}/${post.id}`}
              key={post.id}
              className="blog-post-link"
              onClick={handleClick}
            >
              <div className="blog-post">
                <img
                  src={post.image}
                  alt={postTitle}
                  className="post-image"
                  loading="lazy"
                />
                <h2 className="post-title">{postTitle}</h2>
                <p className="post-excerpt">{postExcerpt}</p>
                <span className="read-more">Read More</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Collection;
